import { useRef, useState, useEffect } from "react";
import { Mouse, Move } from "lucide-react";

const MIN = 1;
const MAX = 6;
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

const PinchIcon = (props) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
    <path d="M9.5 9.5 4.5 4.5" />
    <path d="M4.5 8.5v-4h4" />
    <path d="M14.5 14.5 19.5 19.5" />
    <path d="M19.5 15.5v4h-4" />
    <circle cx="9.5" cy="9.5" r="1.6" fill="currentColor" stroke="none" />
    <circle cx="14.5" cy="14.5" r="1.6" fill="currentColor" stroke="none" />
  </svg>
);

export const ZoomableImage = ({ src, alt, testid }) => {
  const imgRef = useRef(null);
  const [tf, setTf] = useState({ s: 1, tx: 0, ty: 0 });
  const [hint, setHint] = useState(true);
  const tfRef = useRef(tf);
  const pointers = useRef(new Map());
  const gesture = useRef(null);

  const isTouch = typeof window !== "undefined" &&
    ((window.matchMedia && window.matchMedia("(pointer: coarse)").matches) || (navigator.maxTouchPoints || 0) > 0);

  const set = (n) => { tfRef.current = n; setTf(n); };
  const dismissHint = () => setHint(false);

  const zoomAt = (cx, cy, targetS) => {
    const el = imgRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cur = tfRef.current;
    const newS = clamp(targetS, MIN, MAX);
    dismissHint();
    if (newS <= MIN) { set({ s: MIN, tx: 0, ty: 0 }); return; }
    const ratio = newS / cur.s;
    const tx = cur.tx + (cx - rect.left) * (1 - ratio);
    const ty = cur.ty + (cy - rect.top) * (1 - ratio);
    set({ s: newS, tx, ty });
  };

  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
      zoomAt(e.clientX, e.clientY, tfRef.current.s * factor);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const onPointerDown = (e) => {
    imgRef.current?.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const pts = [...pointers.current.values()];
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      gesture.current = { type: "pinch", dist, s: tfRef.current.s };
    } else if (pointers.current.size === 1) {
      gesture.current = { type: "drag", x: e.clientX, y: e.clientY, tx: tfRef.current.tx, ty: tfRef.current.ty };
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (!g) return;
    if (g.type === "pinch" && pointers.current.size >= 2) {
      const pts = [...pointers.current.values()];
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      const cx = (pts[0].x + pts[1].x) / 2;
      const cy = (pts[0].y + pts[1].y) / 2;
      if (g.dist > 0) zoomAt(cx, cy, g.s * (dist / g.dist));
    } else if (g.type === "drag") {
      if (tfRef.current.s <= MIN) return;
      dismissHint();
      set({ ...tfRef.current, tx: g.tx + (e.clientX - g.x), ty: g.ty + (e.clientY - g.y) });
    }
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    imgRef.current?.releasePointerCapture?.(e.pointerId);
    if (pointers.current.size === 1) {
      const p = [...pointers.current.values()][0];
      gesture.current = { type: "drag", x: p.x, y: p.y, tx: tfRef.current.tx, ty: tfRef.current.ty };
    } else if (pointers.current.size === 0) {
      gesture.current = null;
    }
  };

  const onDoubleClick = (e) => {
    e.stopPropagation();
    if (tfRef.current.s > MIN) set({ s: MIN, tx: 0, ty: 0 });
    else zoomAt(e.clientX, e.clientY, 2.5);
  };

  return (
    <>
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        data-testid={testid}
        draggable={false}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={onDoubleClick}
        className="block max-w-full max-h-full object-contain rounded select-none"
        style={{
          transform: `translate(${tf.tx}px, ${tf.ty}px) scale(${tf.s})`,
          transformOrigin: "0 0",
          touchAction: "none",
          cursor: tf.s > MIN ? "grab" : "auto",
          willChange: "transform",
        }}
      />
      {hint && (
        <div
          data-testid="viewer-zoom-hint"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] pointer-events-none flex items-center gap-2 rounded-full bg-black/60 text-white px-3 py-2 shadow-lg backdrop-blur-sm"
        >
          {isTouch ? (
            <PinchIcon />
          ) : (
            <>
              <Mouse size={20} aria-hidden="true" />
              <Move size={20} aria-hidden="true" />
            </>
          )}
        </div>
      )}
    </>
  );
};
